import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { RealtimeProvider } from "@/lib/realtime";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Servers from "@/pages/Servers";
import ServerDetail from "@/pages/ServerDetail";
import Leaderboards from "@/pages/Leaderboards";
import Events from "@/pages/Events";
import Builds from "@/pages/Builds";
import BuildUpload from "@/pages/BuildUpload";
import Store from "@/pages/Store";
import About from "@/pages/About";
import Rules from "@/pages/Rules";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import Community from "@/pages/Community";
import Communities from "@/pages/Communities";
import CommunityDetail from "@/pages/CommunityDetail";
import Messages from "@/pages/Messages";
import FollowRequests from "@/pages/FollowRequests";
import Verify from "@/pages/Verify";
import EventDetail from "@/pages/EventDetail";
import CreatorHub from "@/pages/CreatorHub";
import CreatorApply from "@/pages/CreatorApply";
import CreatorProfile from "@/pages/CreatorProfile";
import SearchResults from "@/pages/SearchResults";

const PageWrap = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();

  // Emergent Google Auth callback — handle session_id in URL fragment
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if ((location.hash || window.location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<Layout />}>
          <Route path="/" element={<PageWrap><Home /></PageWrap>} />
          <Route path="/servers" element={<PageWrap><Servers /></PageWrap>} />
          <Route path="/servers/:key" element={<PageWrap><ServerDetail /></PageWrap>} />
          <Route path="/leaderboards" element={<PageWrap><Leaderboards /></PageWrap>} />
          <Route path="/events" element={<PageWrap><Events /></PageWrap>} />
          <Route path="/events/:slug" element={<PageWrap><EventDetail /></PageWrap>} />
          <Route path="/builds" element={<PageWrap><Builds /></PageWrap>} />
          <Route path="/builds/upload" element={<PageWrap><BuildUpload /></PageWrap>} />
          <Route path="/community" element={<PageWrap><Community /></PageWrap>} />
          <Route path="/communities" element={<PageWrap><Communities /></PageWrap>} />
          <Route path="/communities/:slug" element={<PageWrap><CommunityDetail /></PageWrap>} />
          <Route path="/community/:userId" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/community/@:handle" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/store" element={<PageWrap><Store /></PageWrap>} />
          <Route path="/about" element={<PageWrap><About /></PageWrap>} />
          <Route path="/rules" element={<PageWrap><Rules /></PageWrap>} />
          <Route path="/contact" element={<PageWrap><Contact /></PageWrap>} />
          <Route path="/admin" element={<PageWrap><Admin /></PageWrap>} />
          <Route path="/profile" element={<PageWrap><Profile /></PageWrap>} />
          <Route path="/users/:id" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/messages" element={<PageWrap><Messages /></PageWrap>} />
          <Route path="/follow-requests" element={<PageWrap><FollowRequests /></PageWrap>} />
          <Route path="/verify" element={<PageWrap><Verify /></PageWrap>} />
          <Route path="/get-started" element={<PageWrap><Verify /></PageWrap>} />
          <Route path="/creators" element={<PageWrap><CreatorHub /></PageWrap>} />
          <Route path="/creators/apply" element={<PageWrap><CreatorApply /></PageWrap>} />
          <Route path="/creators/:id" element={<PageWrap><CreatorProfile /></PageWrap>} />
          <Route path="/search" element={<PageWrap><SearchResults /></PageWrap>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <BrowserRouter>
          <AnimatedRoutes />
          <Toaster theme="dark" position="bottom-right" />
        </BrowserRouter>
      </RealtimeProvider>
    </AuthProvider>
  );
}

export default App;
